import { logger } from '@/utils/logger';
import { readThrough, registerSender, writeThrough } from '@shared/offline';

import { endSession, getToken } from './auth';
import { forgetInFlightGets, shareInFlight } from './inflightGet';
import {
  DEFAULT_REQUEST_TIMEOUT_MS,
  RequestTimeoutError,
  armTimeout,
  resolveTimeout,
} from './requestTimeout';

import type { ApiError } from './requestTypes';

export { DEFAULT_REQUEST_TIMEOUT_MS } from './requestTimeout';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ApiResponse<T> {
  data: T;
}

interface ApiHeaders {
  'Content-Type'?: string;
  Authorization?: string;
}

export type { ApiError } from './requestTypes';

export interface ApiRequestOptions {
  params?: Record<string, string> | undefined;
  signal?: AbortSignal;
  /** Bypass a cache-first ttl: read network-first, store the reply, fall back to the cached copy offline. */
  refresh?: boolean | undefined;
  /**
   * Override the default request timeout, in milliseconds.
   *
   * Pass a larger number for endpoints that are legitimately slow, or `null`
   * to opt out entirely (long-lived streams).
   */
  timeoutMs?: number | null;
}

export interface ApiClient {
  get<T>(endpoint: string, options?: ApiRequestOptions): Promise<ApiResponse<T>>;
  post<T>(endpoint: string, data: unknown, options?: ApiRequestOptions): Promise<ApiResponse<T>>;
  put<T>(endpoint: string, data: unknown, options?: ApiRequestOptions): Promise<ApiResponse<T>>;
  patch<T>(endpoint: string, data: unknown, options?: ApiRequestOptions): Promise<ApiResponse<T>>;
  delete<T>(
    endpoint: string,
    options?: ApiRequestOptions & { data?: unknown }
  ): Promise<ApiResponse<T>>;
  /** Returns the raw Response for streaming/SSE. Auth and 401 handling still apply. */
  fetchRaw(endpoint: string, options?: globalThis.RequestInit): Promise<Response>;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const getHeaders = (): ApiHeaders => {
  const token = getToken();

  logger.debug('🌐 API getHeaders() called:');
  logger.debug('- Token available:', !!token);

  const headers: ApiHeaders = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;

    logger.info('✅ Authorization header added');
  } else {
    logger.info('❌ No token available, Authorization header not added');
  }

  return headers;
};

/** Assemble the RequestInit handed to fetch, with auth and credentials. */
const buildConfig = (
  options: globalThis.RequestInit,
  signal: AbortSignal
): globalThis.RequestInit => {
  const headers = getHeaders();

  // FormData: let the browser set Content-Type with the multipart boundary
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  return {
    ...options,
    credentials: 'include',
    signal,
    headers: {
      ...headers,
      ...(options.headers as Record<string, string>),
    },
  };
};

/** Run the request, translating a timeout abort into a named error. */
const runRequest = async (
  url: string,
  config: globalThis.RequestInit,
  timeout: ReturnType<typeof armTimeout>,
  timeoutMs: number | null
): Promise<Response> => {
  // Already cancelled before we started — don't open a connection we know is
  // doomed. Real fetch rejects on a pre-aborted signal, but only after the
  // request is constructed; short-circuiting keeps that off the network.
  if (timeout.signal.aborted) {
    timeout.settle();
    throw timeout.signal.reason instanceof Error
      ? timeout.signal.reason
      : new Error('Request aborted');
  }

  try {
    return await fetch(url, config);
  } catch (err) {
    // Surface the timeout as itself; anything else (including the caller's
    // own abort) propagates untouched.
    throw timeout.timedOut() ? new RequestTimeoutError(url, timeoutMs ?? 0) : err;
  } finally {
    timeout.settle();
  }
};

/** Throw an enriched error for 401s and other non-OK responses. */
const assertResponseOk = async (response: Response): Promise<void> => {
  if (response.status === 401) {
    // The server rejected the session. End it without wiping the offline
    // store: queued work must survive re-login.
    endSession();
    // The status travels with the error: the flusher must tell a rejected
    // session from a network failure, and they are the same shape otherwise.
    const unauthenticated = new Error('Authentication required') as ApiError;
    unauthenticated.status = 401;
    throw unauthenticated;
  }
  if (response.ok) return;

  const errorBody = await response.json().catch(() => ({}));
  const message =
    (errorBody as Record<string, string>).detail ||
    (errorBody as Record<string, string>).message ||
    `HTTP error! status: ${response.status}`;
  const error = new Error(message) as ApiError;
  error.status = response.status;
  error.body = errorBody;
  error.detail = (errorBody as { detail?: unknown }).detail ?? errorBody;
  throw error;
};

/**
 * Custom fetch wrapper with auth headers, credentials, and error interceptors.
 *
 * The timeout covers time-to-response, not time-to-consume-body. That is the
 * right boundary for a function returning a `Response`: it bounds a server
 * that never answers, while leaving streamed bodies (SSE via `fetchRaw`)
 * free to stay open as long as they need.
 */
const fetchWithInterceptors = async (
  url: string,
  options: globalThis.RequestInit = {},
  timeoutMs: number | null = DEFAULT_REQUEST_TIMEOUT_MS
): Promise<Response> => {
  const timeout = armTimeout(url, timeoutMs, options.signal ?? undefined);
  const isWrite = (options.method ?? 'GET').toUpperCase() !== 'GET';
  try {
    const response = await runRequest(
      url,
      buildConfig(options, timeout.signal),
      timeout,
      timeoutMs
    );
    await assertResponseOk(response);
    return response;
  } finally {
    // A read that started before this write may answer with pre-write state;
    // no GET issued from here on may join it.
    if (isWrite) forgetInFlightGets();
  }
};

/** Safely parse JSON response, handling empty bodies. */
const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  const text = await response.text();
  if (!text || text.trim() === '') {
    return null as T;
  }
  return JSON.parse(text) as T;
};

/** Prepare fetch options for methods that accept a body (POST/PUT/PATCH). */
const buildBodyInit = (
  method: string,
  data: unknown,
  options?: ApiRequestOptions
): globalThis.RequestInit => {
  const isFormData = data instanceof FormData;
  const init: globalThis.RequestInit = {
    method,
    body: isFormData ? (data as globalThis.BodyInit) : JSON.stringify(data),
  };
  if (options?.signal) {
    init.signal = options.signal;
  }
  return init;
};

/**
 * Append `params` to an endpoint.
 *
 * `apiGet` builds the url the offline cache keys on and `api.get` builds the
 * one that reaches `fetch`; sharing this is what keeps the two identical.
 */
const withParams = (endpoint: string, params?: Record<string, string>): string => {
  if (!params) return endpoint;
  const searchParams = new URLSearchParams(params);
  return `${endpoint}${endpoint.includes('?') ? '&' : '?'}${searchParams.toString()}`;
};

/**
 * Resolve a write's final url and the options left to send it with.
 *
 * The offline queue replays a write from the url it was given, so the params
 * must already be on that url — handing the queue the bare endpoint would
 * replay a scoped write (a board card's "new chat") unscoped.
 */
const splitParams = <O extends ApiRequestOptions>(
  endpoint: string,
  options?: O
): { url: string; sendOptions: O | undefined } => ({
  url: withParams(endpoint, options?.params),
  sendOptions: options ? { ...options, params: undefined } : undefined,
});

// ---------------------------------------------------------------------------
// Core API client
// ---------------------------------------------------------------------------

/**
 * Core API client with auth headers, 401 interceptor, and JSON parsing.
 *
 * All methods automatically attach the Bearer token, handle FormData
 * content-type detection, and log out on 401 responses. Error responses
 * extract detail messages from the response body.
 */
export const api: ApiClient = {
  async get<T>(endpoint: string, options?: ApiRequestOptions): Promise<ApiResponse<T>> {
    const url = withParams(endpoint, options?.params);
    const timeoutMs = resolveTimeout(options);
    const send = async (): Promise<ApiResponse<T>> => {
      const init: globalThis.RequestInit = { method: 'GET' };
      if (options?.signal) {
        init.signal = options.signal;
      }
      const response = await fetchWithInterceptors(url, init, timeoutMs);
      return { data: await parseJsonResponse<T>(response) };
    };
    // A caller's own signal must not be able to abort a request others joined.
    if (options?.signal) return send();
    return shareInFlight(`${timeoutMs} ${url}`, send);
  },

  async post<T>(
    endpoint: string,
    data: unknown,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    const response = await fetchWithInterceptors(
      withParams(endpoint, options?.params),
      buildBodyInit('POST', data, options),
      resolveTimeout(options)
    );
    const responseData = await parseJsonResponse<T>(response);
    return { data: responseData };
  },

  async put<T>(
    endpoint: string,
    data: unknown,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    const response = await fetchWithInterceptors(
      withParams(endpoint, options?.params),
      buildBodyInit('PUT', data, options),
      resolveTimeout(options)
    );
    const responseData = await parseJsonResponse<T>(response);
    return { data: responseData };
  },

  async patch<T>(
    endpoint: string,
    data: unknown,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    const response = await fetchWithInterceptors(
      withParams(endpoint, options?.params),
      buildBodyInit('PATCH', data, options),
      resolveTimeout(options)
    );
    const responseData = await parseJsonResponse<T>(response);
    return { data: responseData };
  },

  async delete<T>(
    endpoint: string,
    options?: ApiRequestOptions & { data?: unknown }
  ): Promise<ApiResponse<T>> {
    const requestInit: globalThis.RequestInit = {
      method: 'DELETE',
    };

    if (options?.signal) {
      requestInit.signal = options.signal;
    }

    if (options?.data) {
      requestInit.body = JSON.stringify(options.data);
    }

    const response = await fetchWithInterceptors(
      withParams(endpoint, options?.params),
      requestInit,
      resolveTimeout(options)
    );
    const responseData = await parseJsonResponse<T>(response);
    return { data: responseData };
  },

  async fetchRaw(endpoint: string, options?: globalThis.RequestInit): Promise<Response> {
    return fetchWithInterceptors(endpoint, options ?? {});
  },
};

// ---------------------------------------------------------------------------
// Envelope-unwrapping helpers
//
// The Conway API wraps responses as { status: "success", data: <payload> }.
// These helpers fetch and unwrap in one step, returning <payload> directly.
// Use these as a drop-in replacement for the legacy apiCall() pattern.
// ---------------------------------------------------------------------------

interface ApiEnvelope<T> {
  status: string;
  data: T;
  warnings?: string[];
}

/** Callback invoked when the API returns warnings alongside a successful response. */
let _onWarnings: ((warnings: string[]) => void) | null = null;

/** Register a global handler for API response warnings (e.g., show toast). */
export function onApiWarnings(handler: (warnings: string[]) => void): void {
  _onWarnings = handler;
}

/** Unwrap envelope and surface any warnings via the registered handler. */
function _unwrap<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.warnings?.length && _onWarnings) {
    _onWarnings(envelope.warnings);
  }
  return envelope.data;
}

// The flusher replays queued writes through this raw sender, which goes to
// the network directly: routing it through the write helpers would re-enqueue.
registerSender(async (method, url, body) => {
  const response =
    method === 'POST'
      ? await api.post<ApiEnvelope<unknown>>(url, body)
      : method === 'PUT'
        ? await api.put<ApiEnvelope<unknown>>(url, body)
        : method === 'PATCH'
          ? await api.patch<ApiEnvelope<unknown>>(url, body)
          : await api.delete<ApiEnvelope<unknown>>(url, { data: body });
  return _unwrap(response.data);
});

/**
 * Fetches data from an API endpoint and unwraps the `{ status, data }` envelope.
 *
 * @param endpoint - The API endpoint URL.
 * @param options - Optional request options (params, signal).
 * @returns The unwrapped data payload.
 */
export async function apiGet<T>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
  const url = withParams(endpoint, options?.params);
  // The params are already on `url`; drop them so `api.get` cannot append them
  // twice. `refresh` is the read layer's, not the network's.
  const fetchOptions: ApiRequestOptions | undefined = options
    ? { ...options, params: undefined, refresh: undefined }
    : undefined;
  // A cache-first hit resolves without consulting `options.signal`, so an
  // aborted request can still settle from the cache (follow-up).
  return readThrough(
    url,
    async () => {
      const response = await api.get<ApiEnvelope<T>>(url, fetchOptions);
      return _unwrap(response.data);
    },
    { refresh: options?.refresh }
  );
}

/**
 * Posts data to an API endpoint and unwraps the `{ status, data }` envelope.
 *
 * @param endpoint - The API endpoint URL.
 * @param data - The request body payload.
 * @param options - Optional request options (params, signal).
 * @returns The unwrapped data payload.
 */
export async function apiPost<T>(
  endpoint: string,
  data: unknown,
  options?: ApiRequestOptions
): Promise<T> {
  const { url, sendOptions } = splitParams(endpoint, options);
  return writeThrough<T>({ method: 'POST', url, body: data }, async () =>
    _unwrap((await api.post<ApiEnvelope<T>>(url, data, sendOptions)).data)
  );
}

/**
 * Sends a PUT request and unwraps the `{ status, data }` envelope.
 *
 * @param endpoint - The API endpoint URL.
 * @param data - The request body payload.
 * @param options - Optional request options (params, signal).
 * @returns The unwrapped data payload.
 */
export async function apiPut<T>(
  endpoint: string,
  data: unknown,
  options?: ApiRequestOptions
): Promise<T> {
  const { url, sendOptions } = splitParams(endpoint, options);
  return writeThrough<T>({ method: 'PUT', url, body: data }, async () =>
    _unwrap((await api.put<ApiEnvelope<T>>(url, data, sendOptions)).data)
  );
}

/**
 * Sends a PATCH request and unwraps the `{ status, data }` envelope.
 *
 * @param endpoint - The API endpoint URL.
 * @param data - The request body payload.
 * @param options - Optional request options (params, signal).
 * @returns The unwrapped data payload.
 */
export async function apiPatch<T>(
  endpoint: string,
  data: unknown,
  options?: ApiRequestOptions
): Promise<T> {
  const { url, sendOptions } = splitParams(endpoint, options);
  return writeThrough<T>({ method: 'PATCH', url, body: data }, async () =>
    _unwrap((await api.patch<ApiEnvelope<T>>(url, data, sendOptions)).data)
  );
}

/**
 * Sends a DELETE request and unwraps the `{ status, data }` envelope.
 *
 * @param endpoint - The API endpoint URL.
 * @param options - Optional request options (params, signal, data body).
 * @returns The unwrapped data payload.
 */
export async function apiDelete<T>(
  endpoint: string,
  options?: ApiRequestOptions & { data?: unknown }
): Promise<T> {
  const { url, sendOptions } = splitParams(endpoint, options);
  return writeThrough<T>({ method: 'DELETE', url, body: options?.data }, async () =>
    _unwrap((await api.delete<ApiEnvelope<T>>(url, sendOptions)).data)
  );
}

// ---------------------------------------------------------------------------
// Utility: build URL query string from a params object.
// Skips falsy values. Arrays are joined with commas.
// ---------------------------------------------------------------------------

type ParamValue = string | number | boolean | string[] | null | undefined;

/**
 * Builds a URL query string from a params object.
 *
 * Skips null, undefined, and empty values. Arrays are joined with commas.
 * Returns an empty string if no params are provided.
 *
 * @param params - Optional key-value pairs to serialize. Values can be strings, numbers, booleans, string arrays, null, or undefined.
 * @returns The serialized query string without a leading '?', or empty string.
 */
export function buildSearchParams(params?: Record<string, ParamValue>): string {
  if (!params) return '';

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      if (value.length > 0) searchParams.set(key, value.join(','));
    } else if (value) {
      searchParams.set(key, String(value));
    }
  }
  return searchParams.toString();
}

// ---------------------------------------------------------------------------
// Re-exports: LLM providers (extracted to llmProvidersApi.ts)
// ---------------------------------------------------------------------------

export { llmProviders } from './llmProvidersApi';
export type {
  LLMProvider,
  CreateLLMProviderRequest,
  UpdateLLMProviderRequest,
} from './llmProvidersApi';

// ---------------------------------------------------------------------------
// Re-exports: model categories (extracted to modelCategoriesApi.ts)
// ---------------------------------------------------------------------------

export { modelCategories } from './modelCategoriesApi';
export type { ModelCategory } from './modelCategoriesApi';

export default api;
